import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { Student } from '../../student';
import { StudentService } from '../../services/student.service';
import { SchoolContextService } from '../../services/school-context.service';
import { StudentDialogComponent, StudentDialogData } from '../student-dialog/student-dialog.component';
import { EnrollmentService } from '../../services/enrollment.service';
import { GradesService } from '../../services/grades.service';
import { FinanceService } from '../../services/finance.service';
import { AttendanceService } from '../../services/attendance.service';
import { Enrollment } from '../../models/enrollment.model';
import { MarkSheet } from '../../models/grades.model';
import { FeePaymentRecord, StudentFeeAccount } from '../../models/finance.model';
import { AttendanceDayRecord, AttendanceStatusCode } from '../../models/attendance.model';

export type StudentDetailSection =
  | 'general'
  | 'academic'
  | 'address'
  | 'family'
  | 'medical'
  | 'comments'
  | 'documents'
  | 'application';

/** One row in the academic “Grades” table (from mark sheets). */
export interface StudentGradeSummaryRow {
  examTitle: string;
  academicYear: string;
  className: string;
  obtained: number;
  max: number;
  percent: number;
  letterGrade: string;
  rank: number;
}

@Component({
  selector: 'app-student-detail',
  templateUrl: './student-detail.component.html',
  styleUrls: ['./student-detail.component.css']
})
export class StudentDetailComponent implements OnInit, OnDestroy {

  loading = true;
  loadError: string | null = null;
  student: Student | null = null;
  activeSection: StudentDetailSection = 'general';

  enrollments: Enrollment[] = [];
  gradeSummaries: StudentGradeSummaryRow[] = [];
  feeLedger: StudentFeeAccount | null = null;
  todayAttendanceDate: string | null = null;
  todayAttendanceStatus: AttendanceStatusCode | null = null;

  /** Sections that still use the “coming soon” placeholder card. */
  private readonly placeholderSections: StudentDetailSection[] = [
    'comments',
    'documents',
    'application'
  ];

  readonly sections: { id: StudentDetailSection; label: string; icon: string }[] = [
    { id: 'general', label: 'General info', icon: 'badge' },
    { id: 'academic', label: 'Academic info', icon: 'menu_book' },
    { id: 'address', label: 'Address & contact', icon: 'place' },
    { id: 'family', label: 'Family info', icon: 'family_restroom' },
    { id: 'medical', label: 'Medical info', icon: 'medical_services' },
    { id: 'comments', label: 'Comments', icon: 'comment' },
    { id: 'documents', label: 'Documents', icon: 'folder' },
    { id: 'application', label: 'Application info', icon: 'assignment' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private studentService: StudentService,
    public schoolContext: SchoolContextService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private enrollmentService: EnrollmentService,
    private gradesService: GradesService,
    private financeService: FinanceService,
    private attendanceService: AttendanceService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(['/home/students']);
        return;
      }
      this.loadStudent(Number(id));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadStudent(id: number): void {
    this.loading = true;
    this.loadError = null;
    this.enrollments = [];
    this.gradeSummaries = [];
    this.feeLedger = null;
    this.todayAttendanceDate = null;
    this.todayAttendanceStatus = null;

    forkJoin({
      student: this.studentService.getById(id),
      enrollments: this.enrollmentService.getStudentEnrollments(id).pipe(catchError(() => of([] as Enrollment[]))),
      sheets: this.gradesService.getMarkSheets().pipe(catchError(() => of([] as MarkSheet[]))),
      ledger: this.financeService.getLedgerByStudentId(id)
    }).subscribe(
      ({ student, enrollments, sheets, ledger }) => {
        this.student = student;
        this.enrollments = Array.isArray(enrollments) ? enrollments : [];
        this.gradeSummaries = this.buildGradeSummaries(id, sheets);
        this.feeLedger = ledger;
        this.loading = false;
        this.loadTodayAttendance(id, student.className);
      },
      () => {
        this.loading = false;
        this.loadError = 'Could not load student. Check the API or try again.';
      }
    );
  }

  private loadTodayAttendance(studentId: number, className: string | undefined): void {
    if (!className) {
      return;
    }
    const date = this.todayIso();
    this.attendanceService
      .getDayRecords(date, className)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as AttendanceDayRecord[]))
      )
      .subscribe(records => {
        this.todayAttendanceDate = date;
        const row = records.find(r => r.studentId === studentId);
        this.todayAttendanceStatus = row ? row.status : null;
      });
  }

  private todayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private buildGradeSummaries(studentId: number, sheets: MarkSheet[]): StudentGradeSummaryRow[] {
    if (!Array.isArray(sheets) || !sheets.length) {
      return [];
    }
    const year = this.schoolContext.getAcademicYear();
    const withStudent = sheets.filter(s => s.rows?.some(r => r.studentId === studentId));
    const byYear = year
      ? withStudent.filter(s => !s.academicYear || s.academicYear === year)
      : withStudent;
    const source = byYear.length ? byYear : withStudent;
    const rows: StudentGradeSummaryRow[] = [];
    for (const sheet of source) {
      const ranked = this.gradesService.rankStudents(sheet);
      const row = ranked.find(r => r.studentId === studentId);
      if (row) {
        rows.push({
          examTitle: sheet.examTitle || 'Exam',
          academicYear: sheet.academicYear || '—',
          className: sheet.className || '—',
          obtained: row.totalObtained,
          max: row.totalMax,
          percent: row.percent,
          letterGrade: row.letterGrade,
          rank: row.rank
        });
      }
    }
    return rows;
  }

  courseLabel(e: Enrollment): string {
    const c = e.course as Record<string, unknown> | undefined;
    if (c && typeof c === 'object') {
      const title = c['title'] ?? c['name'] ?? c['courseName'];
      if (typeof title === 'string' && title.trim()) {
        return title.trim();
      }
    }
    return `Course #${e.courseId}`;
  }

  enrollmentStatusLabel(status: unknown): string {
    if (status == null || status === '') {
      return '—';
    }
    return String(status).replace(/_/g, ' ');
  }

  feeBalance(): number {
    if (!this.feeLedger) {
      return 0;
    }
    if (this.feeLedger.balanceDue != null && !Number.isNaN(Number(this.feeLedger.balanceDue))) {
      return Number(this.feeLedger.balanceDue);
    }
    const expected = Number(this.feeLedger.annualTotal) || 0;
    const paid = Number(this.feeLedger.totalPaid) || 0;
    return Math.max(0, expected - paid);
  }

  recentFeePayments(): FeePaymentRecord[] {
    if (!this.feeLedger?.payments?.length) {
      return [];
    }
    return [...this.feeLedger.payments]
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))
      .slice(0, 20);
  }

  formatCurrency(n: number | null | undefined): string {
    if (n == null || Number.isNaN(Number(n))) {
      return '—';
    }
    return Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  formatPercent(n: number): string {
    if (n == null || Number.isNaN(n)) {
      return '—';
    }
    return `${n.toFixed(1)}%`;
  }

  attendanceStatusLabel(code: AttendanceStatusCode | null): string {
    if (!code) {
      return '—';
    }
    const map: Record<AttendanceStatusCode, string> = {
      PRESENT: 'Present',
      ABSENT: 'Absent',
      LATE: 'Late',
      EXCUSED: 'Excused'
    };
    return map[code] || code;
  }

  setSection(s: StudentDetailSection): void {
    this.activeSection = s;
  }

  displayName(s: Student): string {
    const parts = [s.salutation, s.firstName, s.middleName, s.lastName, s.suffix].filter(Boolean);
    if (parts.length) {
      return parts.join(' ');
    }
    return [s.firstName, s.lastName].filter(Boolean).join(' ') || 'Student';
  }

  formalName(s: Student): string {
    return this.displayName(s);
  }

  ageLabel(dobIso?: string): string {
    if (!dobIso) {
      return '—';
    }
    const d = new Date(dobIso);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    let months = now.getMonth() - d.getMonth();
    if (months < 0 || (months === 0 && now.getDate() < d.getDate())) {
      years--;
      months += 12;
    }
    if (now.getDate() < d.getDate()) {
      months--;
    }
    if (months < 0) {
      months += 12;
      years--;
    }
    return `${years} years ${months} months`;
  }

  formatDate(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  dash(v: unknown): string {
    if (v === null || v === undefined || v === '') {
      return '—';
    }
    return String(v);
  }

  goBack(): void {
    this.router.navigate(['/home/students']);
  }

  openEdit(): void {
    if (!this.student) {
      return;
    }
    const data: StudentDialogData = { mode: 'edit', student: { ...this.student } };
    this.dialog
      .open(StudentDialogComponent, {
        width: '720px',
        maxHeight: '90vh',
        data
      })
      .afterClosed()
      .subscribe(saved => {
        if (saved && this.student?.id != null) {
          this.loadStudent(this.student.id);
        }
      });
  }

  onContextChange(): void {
    if (this.student?.id != null) {
      this.loadStudent(this.student.id);
    }
  }

  activeSectionLabel(): string {
    return this.sections.find(s => s.id === this.activeSection)?.label || '';
  }

  isPlaceholderSection(section: StudentDetailSection): boolean {
    return this.placeholderSections.includes(section);
  }
}
